<template>
	<!-- Figma 14254:161609 (drawer) / 14254:162862 (page): the cards 8 apart. -->
	<LayoutFlex padding="0" gap="var(--size-8)">
		<!-- Top card. The drawer opens it with "Order #N" — the dialog's level-1 heading — and the badge; the page
		     has those in its breadcrumb, so its top card is the medication alone. -->
		<OrderInfoCard>
			<template v-if="variant === 'drawer'" #header>
				<LayoutFlex
					flex-direction="row"
					justify-content="space-between"
					align-items="center"
					padding="0"
					gap="var(--size-16)"
				>
					<Text
						:id="headingId"
						class="order-details-content__heading"
						:font="HEADING_FONT"
						color="--text-dark-primary"
						role="heading"
						aria-level="1"
						tabindex="-1"
					>
						Order #{{ model.orderNumber }}
					</Text>
					<Badge v-if="model.badge" :tone="BADGE_TONE[model.badge.tone]" size="md">{{ model.badge.label }}</Badge>
				</LayoutFlex>
			</template>
			<LayoutFlex
				flex-direction="row"
				justify-content="space-between"
				align-items="center"
				padding="0"
				gap="var(--size-12)"
			>
				<LayoutFlex padding="0" gap="var(--size-4)" min-width="0">
					<Text font="--text-14-20-med" :color="KOVA.textSecondary">{{ DETAIL_COPY.medication }}</Text>
					<Text font="--text-18-22-med" color="--text-dark-primary">{{ model.medicationName }}</Text>
				</LayoutFlex>
				<!-- A static packshot: the API has no product image (spec D1). Decorative. -->
				<span class="order-details-content__packshot">
					<img :src="packshot" alt="" width="43" height="59" />
				</span>
			</LayoutFlex>
		</OrderInfoCard>

		<OrderInfoCard
			:title="DETAIL_COPY.deliveryInfo"
			:subtitle="model.estDelivery ?? undefined"
			:subtitle-color="KOVA.textSecondary"
		>
			<OrderTimeline :items="model.timeline" :muted="model.timelineMuted" />
		</OrderInfoCard>

		<OrderInfoCard
			v-if="shippingRows"
			:title="DETAIL_COPY.shippingDetails"
			:subtitle="DETAIL_COPY.addressCount"
			:rows="shippingRows"
			:stacked="variant === 'page'"
		/>

		<OrderInfoCard
			v-if="model.payment"
			:title="DETAIL_COPY.paymentDetails"
			:rows="[{ key: 'card', label: DETAIL_COPY.card, lines: [model.payment.mask] }]"
			:stacked="variant === 'page'"
		>
			<!-- The mobile app draws a mark for Mastercard only (spec D5); any other brand shows the number alone. -->
			<template v-if="model.payment.brand === 'mastercard'" #trailing>
				<img class="order-details-content__brand" :src="mastercard" alt="Mastercard" width="23" height="15" />
			</template>
		</OrderInfoCard>
	</LayoutFlex>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Badge, LayoutFlex, Text } from '@simple-life-app/growth-ui-kova'
import mastercard from '@/assets/orders/mastercard.svg'
import packshot from '@/assets/orders/medication-packshot.webp'
import { KOVA } from '@/designTokens'
import { DETAIL_COPY, type OrderDetailModel } from '@/orders/detailModel'
import OrderInfoCard, { type OrderInfoRow } from './OrderInfoCard.vue'
import OrderTimeline from './OrderTimeline.vue'
import { BADGE_TONE } from './orderGlyphs'

/**
 * The details' cards — "Top card", "Delivery info", "Shipping details", "Payment details" — drawn from
 * `buildOrderDetailModel`: which cards show, every label and every value is the model's. `variant` only
 * changes the layout: the drawer (≥ 1024px) heads the top card with the order number, the page (below)
 * stacks label over value. Never here: price, order date, "Placed on", a tracking number (spec D6).
 */

const props = defineProps<{
	model: OrderDetailModel
	variant: 'page' | 'drawer'
	/** The drawer's level-1 heading id ("Order #N"), which labels the dialog. Unused by the page. */
	headingId: string
}>()

/** Figma "Heading 2" (20 Medium, line 1.2): the DS has 20/26 bold and Kova's 20/24 semi only. */
const HEADING_FONT = 'normal var(--fw-medi) var(--20)/var(--lh-24) var(--font-default)'

const shippingRows = computed<OrderInfoRow[] | null>(() => {
	const shipping = props.model.shipping
	if (!shipping) return null
	return [
		{ key: 'address', label: DETAIL_COPY.address, lines: shipping.addressLines },
		{ key: 'recipient', label: DETAIL_COPY.recipient, lines: shipping.recipientLines },
	]
})
</script>

<style scoped>
/* A focus target after a retry only (tabindex -1), not a control: no ring. */
.order-details-content__heading:focus {
	outline: none;
}

/* Figma "Image container": a 48px `button/secondary/grey` tile, radius 16, the packshot over it. */
.order-details-content__packshot {
	position: relative;
	display: flex;
	flex: none;
	width: var(--size-48);
	height: var(--size-48);
	overflow: hidden;
	border-radius: var(--size-16);
	background: var(--button-inactive);
}

.order-details-content__packshot img {
	/* Figma 14254:161624 / 161625: the image box is 42.56 × 59.3 at (1.84, −8.17), clipped by the tile. The
	   file is Figma's image fill, already stretched to that box (Figma stretches a square export into it). */
	position: absolute;
	top: -8.17px;
	left: 1.84px;
	display: block;
	width: 42.56px;
	height: 59.3px;
}

/* Figma "Symbol": 23.4 × 14.7. */
.order-details-content__brand {
	display: block;
	flex: none;
	width: 23.4px;
	height: auto;
}
</style>
